/**
 * @fileoverview Mapper functions for converting between domain models (DTOs) and view models.
 *
 * Architecture:
 * - DTO (UserSearchResultDTO): Matches API response structure exactly
 * - Domain Model (UserRole): Business entity representing one role assignment
 * - View Models (SectorAdminRow, PlantUserRow): UI-optimized for display
 *
 * This follows the established pattern in the codebase:
 * - DTOs in models/dto/
 * - Domain models in models/
 * - View models in pages/Users/types.ts
 * - Mappers provide transformation between layers.
 */

import { UseHasPermission } from "lib/hooks/useHasPermission";
import type { UserSearchResultDTO } from "models/dto/user";
import {
	OBJECT_ID_MANAGE_USER,
	ROLE_NAME_PLANT_USER,
	ROLE_NAME_SECTOR_ADMIN,
} from "../constants";
import type { PlantUserRow, SectorAdminRow } from "../types";

/**
 * Extracts user identification data from UserSearchResultDTO.userData.
 * Uses fallbacks for defensive programming in case the API response doesn't match the interface.
 */
function extractUserIdentification(
	userData: UserSearchResultDTO["userData"]
): {
	userId: string;
	userName: string;
	userEmail: string;
} {
	return {
		userId: userData.id || "",
		userName: userData.name || userData.contact?.name || "",
		userEmail: userData.contact?.email || "",
	};
}

/**
 * Checks if a userRole's sector is accessible by the current user.
 *
 * Uses sector ID for matching since the API reliably provides IDs.
 * Sector name parameter is kept for API compatibility and potential future use
 * (e.g., logging, debugging, or fallback logic).
 *
 * @param sectorName Sector name (kept for compatibility, not used in matching)
 * @param sectorId Sector ID (primary matching mechanism)
 * @param accessibleSectorIdsAsAdminSet Set of sector IDs the current user can access as admin
 * @returns True if the sector ID is in the accessible set
 */
function isSectorAccessible(
	sectorName: string, // Kept for API compatibility and future extensibility
	sectorId: string,
	accessibleSectorIdsAsAdminSet: Set<string>
): boolean {
	// Primary matching by ID (API reliably provides IDs)
	return accessibleSectorIdsAsAdminSet.has(sectorId);
}

/**
 * Extracts unique non-empty values from an array.
 */
function extractUnique<T>(items: T[]): T[] {
	return [...new Set(items.filter(Boolean))];
}

/**
 * Checks if a plant user role matches a manageable plant.
 * Requires plant ID match and sector match (by ID and/or name if available).
 */
function matchesManageablePlant(
	userRole: { plantId?: string; containmentSectorId: string; containmentSectorName: string },
	plant: { id: string; sectorId: string; sectorName: string }
): boolean {
	// Plant ID must match (required for security)
	if (plant.id !== userRole.plantId) {
		return false;
	}

	// Sector must match: if both have sector ID, they must match
	// If both have sector name, they must match
	// At least one sector identifier must be present
	const hasSectorId = Boolean(plant.sectorId && userRole.containmentSectorId);
	const hasSectorName = Boolean(plant.sectorName && userRole.containmentSectorName);

	if (!hasSectorId && !hasSectorName) {
		return false; // No sector identifiers to match
	}

	const sectorIdMatches = !hasSectorId || plant.sectorId === userRole.containmentSectorId;
	const sectorNameMatches =
		!hasSectorName || plant.sectorName === userRole.containmentSectorName;

	return sectorIdMatches && sectorNameMatches;
}

/**
 * Converts domain models (UserSearchResultDTO[]) to view models.
 *
 * Handles aggregation and grouping for display optimization:
 * - Sector Admins: Aggregates all sectors into one row per user
 * - Plant Users: Creates one row per user per sector
 *
 * Filters results based on current user's permissions:
 * - Sector Admins: See all users (Sector Admins and Plant Users) in their accessible sectors only
 *   - Matching is done by sector ID (API reliably provides IDs)
 * - Plant Users Access Managers (non-Sector Admin): See only Plant Users in their manageable plants
 *
 * @param users Array of user search results from the API
 * @param permissions Unified permission interface with checks, flags, and data
 * @returns Object containing sector admins and plant users view models
 */
export function MapUserSearchResultsToViewModels(
	users: UserSearchResultDTO[],
	permissions: ReturnType<typeof UseHasPermission>
): {
	sectorAdmins: SectorAdminRow[];
	plantUsers: PlantUserRow[];
} {
	const sectorAdminRows: SectorAdminRow[] = [];
	const plantUserRows: PlantUserRow[] = [];

	// Use pre-computed Set from permissions for O(1) lookups
	// Note: We only use sector IDs for matching, not sector names
	const accessibleSectorIdsAsAdminSet = permissions.accessibleSectorIdsAsAdminSet;
	const sectorIdsManageableForPlantUser = new Set(
		permissions.getAccessibleSectorsForObject(OBJECT_ID_MANAGE_USER).map((s) => s.id)
	);

	users.forEach((userItem) => {
		const userData = userItem.userData;
		const userRoles = userItem.userRoles || [];

		// Skip users with no role assignments
		if (userRoles.length === 0) {
			return;
		}

		// Extract user identification data
		const { userId, userName, userEmail } = extractUserIdentification(userData);

		// Separate Sector Admin roles from Plant User roles
		const sectorAdminRoles = userRoles.filter(
			(userRole) => userRole.roleName === ROLE_NAME_SECTOR_ADMIN
		);
		const plantUserRoles = userRoles.filter(
			(userRole) => userRole.roleName === ROLE_NAME_PLANT_USER
		);

		// Create Sector Admin rows (one row per user, aggregating all sectors).
		// Include when user can view Sector Admins table OR when they can view Plant Users (so Add Plant User dialog has existingSectorAdmins for overlap check).
		// Only include sector admin roles in sectors the current user can manage.
		if (sectorAdminRoles.length > 0 && (permissions.canViewSectorAdmins || permissions.canViewPlantUsers)) {
			// Which sector-admin roles to include: filter by "sectors the user dialogs are allowed to see" for this context.
			const relevantRoles = permissions.canViewSectorAdmins
				? (permissions.isSectorAdmin
						? sectorAdminRoles.filter((userRole) =>
								// Sector Admin viewing the table: only sectors they are admin for.
								isSectorAccessible(
									userRole.containmentSectorName,
									userRole.containmentSectorId,
									accessibleSectorIdsAsAdminSet
								)
							)
						: sectorAdminRoles)
				: sectorAdminRoles.filter((userRole) =>
						// Plant Admin only: only sectors where they can add plant users (for Add Plant User overlap check).
						userRole.containmentSectorId &&
						sectorIdsManageableForPlantUser.has(userRole.containmentSectorId)
					);

			if (relevantRoles.length > 0) {
				// Aggregate unique sector names, IDs, and userRoleIds
				const sectors = extractUnique(
					relevantRoles.map((userRole) => userRole.containmentSectorName)
				);
				const sectorIds = extractUnique(
					relevantRoles.map((userRole) => userRole.containmentSectorId)
				);
				const userRoleIds = extractUnique(
					relevantRoles.map((userRole) => userRole.userRoleId)
				);

				sectorAdminRows.push({
					userId,
					name: userName,
					email: userEmail,
					sectors,
					sectorIds,
					userRoleIds,
				});
			}
		}

		// Create Plant User rows (one row per user per sector)
		if (plantUserRoles.length > 0 && permissions.canViewPlantUsers) {
			// Filter based on user type
			// When user is BOTH Sector Admin AND Plant Users Access Manager, combine both filters (union)
			// User can see Plant Users if accessible via Sector Admin OR Plant Users Access Manager permissions
			const relevantRoles = plantUserRoles.filter((userRole) => {
				// Check if accessible via Sector Admin role (sector-based access)
				const accessibleViaSectorAdmin =
					permissions.isSectorAdmin &&
					isSectorAccessible(
						userRole.containmentSectorName, // Kept for compatibility
						userRole.containmentSectorId, // Primary matching value
						accessibleSectorIdsAsAdminSet
					);

				// Check if accessible via Plant Users Access Manager role (plant-based access)
				const accessibleViaPlantUsersAccessManager =
					permissions.isPlantUsersAccessManager &&
					permissions.manageablePlants.some((plant) =>
						matchesManageablePlant(userRole, plant)
					);

				// User can see this Plant User if accessible via either method
				return accessibleViaSectorAdmin || accessibleViaPlantUsersAccessManager;
			});

			// Group user roles by sector to create separate rows per sector
			const rolesBySector = new Map<string, typeof relevantRoles>();
			for (const userRole of relevantRoles) {
				const sectorKey = userRole.containmentSectorId || userRole.containmentSectorName;
				if (!rolesBySector.has(sectorKey)) {
					rolesBySector.set(sectorKey, []);
				}
				const roles = rolesBySector.get(sectorKey);
				if (roles) {
					roles.push(userRole);
				}
			}

			for (const userRolesInSector of rolesBySector.values()) {
				const firstUserRole = userRolesInSector[0];
				const sectorName = firstUserRole.containmentSectorName || "";
				const sectorId = firstUserRole.containmentSectorId || "";

				// Extract unique plant names, IDs, and userRoleIds
				const plants = extractUnique(
					userRolesInSector.map((userRole) => userRole.plantName)
				);
				const plantIds = extractUnique(
					userRolesInSector.map((userRole) => userRole.plantId)
				);
				const userRoleIds = extractUnique(
					userRolesInSector.map((userRole) => userRole.userRoleId)
				);

				plantUserRows.push({
					userId,
					name: userName,
					email: userEmail,
					sector: sectorName,
					sectorId,
					plants,
					plantIds,
					userRoleIds,
				});
			}
		}
	});

	return {
		sectorAdmins: sectorAdminRows,
		plantUsers: plantUserRows,
	};
}

/**
 * Converts a view model row back to UserSearchResultDTO for CRUD operations.
 * Works for both SectorAdminRow and PlantUserRow since they both have userId and userRoleIds.
 */
function mapRowToUserSearchResult(
	row: { userId: string; userRoleIds: string[] },
	allUsers: UserSearchResultDTO[]
): UserSearchResultDTO | null {
	const user = allUsers.find((u) => u.userData.id === row.userId);
	if (!user) return null;

	const userRoleIdsSet = new Set(row.userRoleIds);
	return {
		...user,
		userRoles: user.userRoles.filter((userRole) =>
			userRoleIdsSet.has(userRole.userRoleId)
		),
	};
}

/**
 * Converts a SectorAdminRow view model back to UserSearchResultDTO for CRUD operations.
 */
export function MapSectorAdminRowToUserSearchResult(
	row: SectorAdminRow,
	allUsers: UserSearchResultDTO[]
): UserSearchResultDTO | null {
	return mapRowToUserSearchResult(row, allUsers);
}

/**
 * Converts a PlantUserRow view model back to UserSearchResultDTO for CRUD operations.
 */
export function MapPlantUserRowToUserSearchResult(
	row: PlantUserRow,
	allUsers: UserSearchResultDTO[]
): UserSearchResultDTO | null {
	return mapRowToUserSearchResult(row, allUsers);
}
